import { TFunction } from 'i18next';
import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Color, MathUtils, Vector3 } from 'three';

import { redrawSkyViewWithOptions } from '../../../../../../utils/three/snapshot';
import {
  generateSVGFromSkyView,
  SVGLineData,
  SVGPoiData,
  SVGPointData,
  SVGTextData,
  SVGTriangleData,
} from '../../../../../../utils/three/vectorize-geometry';
import { isDefined } from '../../../../../../utils/variables';
import cameraManager from '../../../../../../viewer/camera-manager';
import { SkyviewOrthographicCamera } from '../../../../../../viewer/cameras/skyview-orthographic-camera';
import { CoordinateManager } from '../../../../../../viewer/coordinate-manager';
import { composeOrthotileWithHelpers } from '../../../../../../viewer/loader/orthotile/compose-orthotile-with-helpers';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../../../../viewer/project-geodata-manager';
import { SkyView } from '../../../../../../viewer/skyview';
import { GlobalMercator } from '../../../../../../viewer/utilities/projections/global-mercator';
import { WmsState } from '../../../../../../viewer/wms-state';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useProgressBar } from '../../../../../hooks/use-progress-bar';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { useOptionalUserState } from '../../../../../state/user-state/user-state';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { Dialog } from '../../../../dialog/dialog';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import {
  RightPanelContent,
  RightPanelFooter,
} from '../../../../skyview/right-menu/components/styles';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { Slider } from '../../../../slider/slider';
import { Stack } from '../../../../stack/stack';
import { reportHelpers } from '../helpers';
import { CadDrawingReportImage, getCroppedCadImage } from './cad-drawing-image-utils';
import { CadDrawingPdfReportPreview, CadDrawingVectorizedPreview } from './cad-drawing-pdf';
import { CadDrawingTitleBlockFields } from './cad-drawing-title-block-fields';
import { exportVectorizedCadDrawing } from './cad-drawing-vectorized-export';

export type CadDrawingReportVector = {
  backgroundImage: CadDrawingReportImage;
  lines: SVGLineData[];
  triangles: SVGTriangleData[];
  texts: SVGTextData[];
  points: SVGPointData[];
  pois: SVGPoiData[];
  width: number;
  height: number;
  backgroundColor?: string;
};

export type CadDrawingReportData = CadDrawingReportImage | CadDrawingReportVector;

export type InfoPanelOptions = {
  isOrthoView: boolean;
  useWhiteBackground: boolean;
  includeDrawingFrame: boolean;
  useVectorizedDrawing: boolean;
  useOriginalLineColor: boolean;
  includeOrthophoto: boolean;
  strokeWidth: number;
  title: string;
  description: string;
  responsible: string;
  freeText: string;
  profileLineName: string;
  totalLength: string;
  uploadedImage?: {
    name: string;
    dataUrl: string;
  };
  preview?: boolean;
};

function optionsReducer(
  state: InfoPanelOptions,
  values: Partial<InfoPanelOptions>,
): InfoPanelOptions {
  return {
    ...state,
    ...values,
  };
}

const nextFrame = (waitTimeMs: number = 0) =>
  new Promise<void>((r) => setTimeout(() => requestAnimationFrame(() => r()), waitTimeMs));

function getDefaultOptions(
  isOrthoView: boolean,
  getName: () => string,
  t: TFunction,
): InfoPanelOptions {
  return {
    includeDrawingFrame: true,
    useWhiteBackground: true,
    useVectorizedDrawing: isOrthoView,
    useOriginalLineColor: true,
    strokeWidth: 1,
    description: '',
    freeText: '',
    profileLineName: '',
    totalLength: '',
    responsible: getName(),
    isOrthoView: isOrthoView,
    includeOrthophoto: true,
    title: t('cadDrawingReport.pdf.rightPanel.titleDefault', { ns: 'skyviewReport' }),
  };
}

export const CadDrawingPdfReportMenu = () => {
  const { t } = useTranslation();
  const { skyview } = useContext(SkyviewContext);
  const userState = useOptionalUserState();
  const resolvedUserName = userState?.getName() ?? '';
  const [reportData, setReportData] = useState<CadDrawingReportData>();
  const [options, dispatchOptions] = useReducer(
    optionsReducer,
    getDefaultOptions(
      cameraManager().getActiveCamera() instanceof SkyviewOrthographicCamera,
      () => resolvedUserName,
      t,
    ),
  );

  useEffect(() => {
    if (options.responsible === '' && resolvedUserName !== '') {
      dispatchOptions({ responsible: resolvedUserName });
    }
  }, [resolvedUserName, options.responsible]);

  const dialog = useDialog();
  const { onProgress, renderProgressBar, isIdle } = useProgressBar({ hideWhenIdle: true });

  const refreshCamera = useCallback(() => {
    dispatchOptions({
      isOrthoView: cameraManager().getActiveCamera() instanceof SkyviewOrthographicCamera,
    });
  }, [dispatchOptions]);

  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, () => refreshCamera());
  useSubscribe(SubscriptionTopic.OnCameraChanged, () => refreshCamera());

  const refreshImage = useCallback(async () => {
    const cadImage = await cadDrawingReportUtils.generateImageUrl(
      skyview,
      {
        ...options,
        useVectorizedDrawing: options.isOrthoView ? options.useVectorizedDrawing : false,
      },
      onProgress,
    );
    setReportData(cadImage);
    return cadImage;
  }, [options, skyview, onProgress]);

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <Stack spacing={1}>
          <InfoBox color="yellow">{t('cadDrawingReport.info', { ns: 'skyviewReport' })}</InfoBox>

          <CheckBox
            checked={options.useVectorizedDrawing}
            disabled={!options.isOrthoView}
            onChange={() =>
              dispatchOptions({ useVectorizedDrawing: !options.useVectorizedDrawing })
            }
          >
            {t('cadDrawingReport.vectorizeGeometry', { ns: 'skyviewReport' })}
          </CheckBox>

          <CheckBox
            checked={options.useWhiteBackground}
            onChange={() => dispatchOptions({ useWhiteBackground: !options.useWhiteBackground })}
          >
            {t('cadDrawingReport.useWhiteBackground', { ns: 'skyviewReport' })}
          </CheckBox>
          <CheckBox
            checked={options.includeDrawingFrame}
            onChange={() => dispatchOptions({ includeDrawingFrame: !options.includeDrawingFrame })}
          >
            {t('cadDrawingReport.includeDrawingFrame', { ns: 'skyviewReport' })}
          </CheckBox>

          {options.useVectorizedDrawing &&
            options.isOrthoView &&
            skyview.isOrthophotoInOrthoViewActive() && (
              <CheckBox
                checked={options.includeOrthophoto}
                onChange={() => dispatchOptions({ includeOrthophoto: !options.includeOrthophoto })}
              >
                {t('cadDrawingReport.includeOrthophoto', { ns: 'skyviewReport' })}
              </CheckBox>
            )}

          {options.useVectorizedDrawing && options.isOrthoView && (
            <CheckBox
              checked={!options.useOriginalLineColor}
              onChange={() =>
                dispatchOptions({ useOriginalLineColor: !options.useOriginalLineColor })
              }
            >
              {t('cadDrawingReport.useBlackLinesAndText', { ns: 'skyviewReport' })}
            </CheckBox>
          )}

          {options.useVectorizedDrawing && options.isOrthoView && (
            <LabelledContainer text={t('cadDrawingReport.lineThickness', { ns: 'skyviewReport' })}>
              {() => (
                <Slider
                  max={1.5}
                  min={0.05}
                  step={0.05}
                  value={options.strokeWidth}
                  onChange={(value) => dispatchOptions({ strokeWidth: value })}
                />
              )}
            </LabelledContainer>
          )}

          <CadDrawingTitleBlockFields values={options} onChange={dispatchOptions} />
        </Stack>
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.4}>
          {renderProgressBar()}
          <Button
            color={'secondary'}
            disabled={!isIdle}
            leftIcon={{ icon: 'print-magnifying-glass' }}
            loading={!isIdle}
            variant={'contained'}
            width="100%"
            onClick={async () => {
              await refreshImage();
              dialog.show();
            }}
          >
            {t('export.preview', { ns: 'skyviewReport' })}
          </Button>

          <Button
            color={'primary'}
            disabled={!isIdle}
            loading={!isIdle}
            variant={'contained'}
            width="100%"
            onClick={async () => {
              const cadImage = await refreshImage();
              await exportReport(cadImage, options, skyview, t, onProgress);
            }}
          >
            {t('exportButton', { ns: 'skyviewReport' })}
          </Button>
        </Stack>
      </RightPanelFooter>

      {isDefined(reportData) &&
        dialog.render(
          <PreviewDialog
            image={reportData}
            options={options}
            skyview={skyview}
            onClose={dialog.hide}
            onProgress={onProgress}
          />,
        )}
    </>
  );
};

const PreviewDialog = (props: {
  onClose: () => void;
  image: CadDrawingReportData;
  options: InfoPanelOptions;
  skyview: SkyView;
  onProgress?: ReturnType<typeof useProgressBar>['onProgress'];
}) => {
  const { t } = useTranslation();
  return (
    <Dialog closeIcon={true} maxHeight={'expand'} onClose={props.onClose}>
      {{
        header: t('cadDrawingReport.dialog.header', { ns: 'skyviewReport' }),
        content: isVectorizedData(props.image) ? (
          <CadDrawingVectorizedPreview
            backgroundColor={props.image.backgroundColor}
            backgroundImage={props.image.backgroundImage}
            height={props.image.height}
            lines={props.image.lines}
            options={props.options}
            points={props.image.points}
            pois={props.image.pois}
            texts={props.image.texts}
            triangles={props.image.triangles}
            width={props.image.width}
          />
        ) : (
          <CadDrawingPdfReportPreview image={props.image} options={props.options} />
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={async () => {
                  props.onClose();
                  await exportReport(
                    props.image,
                    props.options,
                    props.skyview,
                    t,
                    props.onProgress,
                  );
                }}
              >
                {t('exportButton', { ns: 'skyviewReport' })}
              </Button>
              <Button variant="text" onClick={props.onClose}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

// Type guard to check if data is vectorized
function isVectorizedData(data: CadDrawingReportData): data is CadDrawingReportVector {
  return 'lines' in data;
}

export const cadDrawingReportUtils = {
  generateImageUrl: async (
    skyview: SkyView,
    options: InfoPanelOptions,
    onProgress: ReturnType<typeof useProgressBar>['onProgress'],
  ): Promise<CadDrawingReportData> => {
    if (options.useVectorizedDrawing) {
      return await generateVectorizedImage(skyview, options, onProgress);
    } else {
      return await generateRasterImage(skyview, options);
    }
  },
};

async function generateVectorizedImage(
  skyview: SkyView,
  options: InfoPanelOptions,
  onProgress: ReturnType<typeof useProgressBar>['onProgress'],
): Promise<CadDrawingReportVector> {
  try {
    // Step 1: Set progress to 0/1 when export starts
    onProgress({ current: 0, total: 1 });

    // Generate background image from active scene
    const clearColor: number | undefined = options?.useWhiteBackground ? 0xffffff : undefined;
    redrawSkyViewWithOptions(skyview, { clearColor, hideAssets: true });

    const camera = cameraManager().getActiveCamera();

    skyview.renderer!.clear();

    if (skyview.renderer && WmsState.instance.wmsMapEnabled && camera) {
      WmsState.instance.render(skyview.renderer, camera.getTHREECamera());
    }

    const backgroundImageDataUrl = skyview.renderer!.domElement.toDataURL('image/png');
    const croppedBackgroundImage = await getCroppedCadImage(backgroundImageDataUrl);

    const progressVectorMin = 0.1;
    const progressVectorMax = skyview.isOrthophotoInOrthoViewActive() ? 0.5 : 0.9;
    const progressVectorRange = progressVectorMax - progressVectorMin;

    const progressOrthophotoMin = 0.5;
    const progressOrthophotoMax = 0.9;
    const progressOrthophotoRange = progressOrthophotoMax - progressOrthophotoMin;

    const getProgressCallback =
      (min: number, max: number, range: number) => (progress: number, label?: string) => {
        const clampedProgress = MathUtils.clamp(min + progress * range, min, max);
        onProgress({ current: clampedProgress, total: 1, label });
      };

    const onVectorProgress = getProgressCallback(
      progressVectorMin,
      progressVectorMax,
      progressVectorRange,
    );
    const onOrthophotoProgress = getProgressCallback(
      progressOrthophotoMin,
      progressOrthophotoMax,
      progressOrthophotoRange,
    );

    // Step 2: Generate SVG data with progress tracking (0.1 -> 0.5)
    const imageData = await generateSVGFromSkyView(
      skyview,
      {
        useOriginalLineColor: options.useOriginalLineColor,
        useWhiteBackground: options.useWhiteBackground,
        width: croppedBackgroundImage.width,
        height: croppedBackgroundImage.height,
      },
      onVectorProgress,
    );

    // Step 3: Compose orthotile with progress tracking (0.5 -> 0.9)
    const coordinateBoundaries = croppedBackgroundImage.coordinateBoundaries;

    const orthoData = ProjectGeodataManager.instance().getActivOrthotileGeodata();
    const projection = CoordinateManager.getInstance().getProjectProjection();
    const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
    // Composed from XYZ tiles; for COG orthotiles the report keeps the screenshot background
    const highResolutionOrthophotoUrl =
      options.includeOrthophoto &&
      skyview.isOrthophotoInOrthoViewActive() &&
      orthoData?.format === 'XYZ' &&
      orthoData.model
        ? await composeOrthotileWithHelpers(
            coordinateBoundaries,
            {
              orthotileProject: orthoData.model,
              globalMercator: new GlobalMercator(orthoData.model.tileSize),
              store: orthoData.store,
              projection,
              origo,
            },
            onOrthophotoProgress,
          )
        : undefined;

    return {
      ...imageData,
      backgroundImage: {
        ...croppedBackgroundImage,
        backgroundColor: imageData.backgroundColor!,
        highResolutionOrthophotoUrl,
      },
    };
  } catch (error) {
    console.error('Failed to generate vectorized image:', error);
    throw new Error('Vectorized image generation failed');
  } finally {
    onProgress({ current: 1, total: 1, label: null });
  }
}

async function generateRasterImage(
  skyview: SkyView,
  options: InfoPanelOptions,
): Promise<CadDrawingReportImage> {
  const clearColor: number | undefined = options?.useWhiteBackground ? 0xffffff : undefined;

  redrawSkyViewWithOptions(skyview, { clearColor });
  try {
    const getSkyviewScreenshot = (window as any).getSkyviewScreenshot;
    if (typeof getSkyviewScreenshot !== 'function') {
      throw new Error('SkyviewCanvasContainer screenshot method not available');
    }

    const backgroundColor = options.useWhiteBackground
      ? '#ffffff'
      : '#' + skyview.renderer!.getClearColor(new Color()).getHexString();

    const cadImage = (await getSkyviewScreenshot()) as string;
    const { ...croppedImageData } = await getCroppedCadImage(cadImage);
    return {
      ...croppedImageData,
      backgroundColor,
    };
  } catch (error) {
    console.error('Failed to capture skyview screenshot:', error);
    throw new Error('Screenshot capture failed');
  }
}

async function exportReport(
  image: CadDrawingReportData,
  options: InfoPanelOptions,
  skyview: SkyView,
  t: TFunction,
  onProgress?: ReturnType<typeof useProgressBar>['onProgress'],
) {
  try {
    if (isVectorizedData(image)) {
      // Validate vectorized data dimensions
      if (
        !Number.isFinite(image.width) ||
        !Number.isFinite(image.height) ||
        image.width <= 0 ||
        image.height <= 0
      ) {
        throw new Error(
          `Invalid vectorized image dimensions: width=${image.width}, height=${image.height}`,
        );
      }

      if (onProgress) {
        const numberOfObjects =
          image.lines.length +
          image.triangles.length +
          image.texts.length +
          image.points.length +
          image.pois.length;
        const label = t('cadDrawingReport.finishingReport', {
          ns: 'skyviewReport',
          count: numberOfObjects,
        });
        onProgress({ current: 0.95, total: 1, label });

        // Give react time to refresh and render the progress bar before main thread gets blocked by React-Pdf rendering.
        await nextFrame(200);
      }

      await exportVectorizedCadDrawing(
        image.lines,
        image.triangles,
        image.texts,
        image.points,
        image.pois,
        image.width,
        image.height,
        options,
        image.backgroundImage,
        image.backgroundColor,
      );
    } else {
      await reportHelpers.exportCadDrawingPdfReport(image, options, image.backgroundColor);
    }
  } catch (error) {
    console.error('Export failed:', error);
    publish(SubscriptionTopic.ToastrMessage, {
      message: t('exportError', { ns: 'skyviewReport' }) as string,
      type: 'error',
    });
  } finally {
    onProgress?.({ current: 1, total: 1, label: null });
  }
}
